import { Time } from "store/store-types";
import { Article } from "types/article";
import { BookingChange } from "types/booking-change";
import { BookingStatus } from "types/booking-status";
import { ChargeType } from "types/charge-type";
import { Contact } from "types/contact";
import { Customer } from "types/customer";
import { BookingMenu } from "types/menu";
import { InquiryValues } from "types/inquiry-values";
import { ChargeStatus, PaymentEvent } from "types/payment";
import { WaitListOffer } from "types/wait-list-offer";
import { WebRule } from "types/web-rule";
import { parseDate, parseDateWithTimeZone } from "utils/moment-utils";
import BookingSubStatusEnum from "types/booking-sub-status";

export class WebBooking {
    guid: string;
    unitId: number;
    start: IMoment;
    end: IMoment;
    status: BookingStatus;
    subStatus: BookingSubStatusEnum;
    guests: number;
    guestsChildren: number;
    message: string;
    createdDate: IMoment;
    changeDate: IMoment;
    recoupTime: number;
    articles: Article[];
    nonTableArticles: Article[];
    chargeType: ChargeType;
    chargeId: string;
    chargeShouldPayAmount: number;
    chargeShouldPayVat: number;
    chargeAuthorizedAmount: number;
    chargePayedAmount: number;
    chargeRefundedAmount: number;
    chargeStatus: ChargeStatus;
    chargePaymentLinkExpirationDate: IMoment;
    contactId: number;
    contact: Contact;
    customer: Customer;
    menus: BookingMenu[];
    isInUpdate: boolean;
    globalBookingNumber: string;
    language: string;
    cancelAllowedBeforeUtc: IMoment | null;
    webBookingRules: WebRule[] | null;
    history: BookingChange[] | null;
    waitListOffers: WaitListOffer[] | null;
    paymentEvents: PaymentEvent[];
    inquiryValues?: InquiryValues;

    constructor(values?: Partial<WebBooking>) {
        this.guid = values?.guid ?? "";
        this.unitId = values?.unitId ?? 0;
        this.start = parseDate(values?.start);
        this.end = parseDate(values?.end);
        this.status = values?.status ?? BookingStatus.Unset;
        this.subStatus = values?.subStatus ?? BookingSubStatusEnum.None;
        this.guests = values?.guests ?? 0;
        this.guestsChildren = values?.guestsChildren ?? 0;
        this.message = values?.message ?? "";
        this.createdDate = parseDate(values?.createdDate);
        this.changeDate = parseDate(values?.changeDate);
        this.recoupTime = values?.recoupTime ?? 0;
        this.articles = values?.articles?.map((a) => new Article(a)) ?? [];
        this.nonTableArticles = values?.nonTableArticles?.map((a) => new Article(a)) ?? [];
        this.chargeType = values?.chargeType ?? ChargeType.None;
        this.chargeId = values?.chargeId ?? "";
        this.chargeShouldPayAmount = values?.chargeShouldPayAmount ?? 0;
        this.chargeShouldPayVat = values?.chargeShouldPayVat ?? 0;
        this.chargeAuthorizedAmount = values?.chargeAuthorizedAmount ?? 0;
        this.chargePayedAmount = values?.chargePayedAmount ?? 0;
        this.chargeRefundedAmount = values?.chargeRefundedAmount ?? 0;
        this.chargeStatus = values?.chargeStatus ?? ChargeStatus.Unknown;
        this.chargePaymentLinkExpirationDate = parseDate(values?.chargePaymentLinkExpirationDate);
        this.contactId = values?.contactId ?? 0;
        this.contact = values?.contact ?? ({} as Contact);
        this.customer = values?.customer ?? {};
        this.menus = values?.menus ?? [];
        this.isInUpdate = values?.isInUpdate ?? false;
        this.globalBookingNumber = values?.globalBookingNumber ?? "";
        this.language = values?.language ?? "en";
        this.cancelAllowedBeforeUtc = values?.cancelAllowedBeforeUtc
            ? parseDateWithTimeZone(values.cancelAllowedBeforeUtc)
            : null;
        this.webBookingRules = values?.webBookingRules?.map((rule) => new WebRule(rule)) ?? null;
        this.history = values?.history?.map((change) => new BookingChange(change)) ?? null;
        this.waitListOffers =
            values?.waitListOffers?.map((offer) => new WaitListOffer(offer)) ?? null;
        this.paymentEvents = values?.paymentEvents?.map((p) => new PaymentEvent(p)) ?? [];
        this.inquiryValues = new InquiryValues(values?.inquiryValues);
    }

    /**
     * Returns the adjusted end time, subtracting the recoup time (städtid).
     * Used to display the time guests see for their booking.
     */
    public displayEndTime(): IMoment {
        return this.end.clone().subtract(this.recoupTime, "minutes");
    }

    public getBookTimes(): Time[] {
        return this.articles?.map((article) => ({
            ruleId: article.ruleId,
            start: article.start,
            end: article.end,
            sectionId: article.sectionId,
        }));
    }

    public isActivityBooking(): boolean {
        if (!this.articles) return false;
        const uniqueRuleIds = new Set(this.articles.map((a) => a.ruleId));
        return uniqueRuleIds.size > 1;
    }

    public isWaitList(): boolean {
        return this.status === BookingStatus.WaitList;
    }

    public isFinalized(): boolean {
        return this.status === BookingStatus.Web;
    }

    public isWebBooking(): boolean {
        return Boolean(this.status === BookingStatus.Web && this.articles?.at(0)?.ruleId);
    }

    public isInquiry(): boolean {
        return this.status === BookingStatus.Inquiry;
    }

    public isInquiryProposal(): boolean {
        return this.subStatus === BookingSubStatusEnum.ProposeChanges;
    }

    public proposalAcceptedByGuest(): boolean {
        return this.subStatus === BookingSubStatusEnum.AcceptedByGuest;
    }

    public proposalRejectedByGuest(): boolean {
        return this.subStatus === BookingSubStatusEnum.RejectedByGuest;
    }

    public proposalTimeout(): boolean {
        return this.subStatus === BookingSubStatusEnum.ProposalTimeout;
    }

    public proposalRejectedByStaff(): boolean {
        return this.subStatus === BookingSubStatusEnum.RejectedByStaff;
    }

    public anyNonCompleteInquirySubStatus(): boolean {
        return (
            this.subStatus !== BookingSubStatusEnum.None &&
            this.subStatus !== BookingSubStatusEnum.ConfirmedByStaff
        );
    }

    public hasMenus(): boolean {
        return this?.menus?.length > 0;
    }

    public requiresMenuPayment(): boolean {
        return Boolean(this.webBookingRules?.some((rule) => rule.requireMenuPayment));
    }

    /**
     * True once the guest has handed the booking over to the restaurant, whether
     * that made it a confirmed web booking, a wait list signup or a request
     * waiting for review. Mirrors what the finalize step produces per flow.
     */
    public isCompleted(): boolean {
        return this.isFinalized() || ((this.isWaitList() || this.isInquiry()) && this.hasContact());
    }

    public hasContact(): boolean {
        return Boolean(
            this.contact &&
            this.contact.fname &&
            this.contact.lname &&
            this.contact.email &&
            this.contact.mobile
        );
    }
}
